import React, { useEffect, useMemo, useState } from 'react';
import { ApolloProvider, useLazyQuery, useQuery } from '@apollo/client';
import { CustomerComponent, DisplayMode, Environments } from '@atlassian/omni-channel';
import config from 'appconfig';
import { omniChannelApolloClient } from './graphql/client';
import {
  getChannelPlatformTokenByClientName,
  getChatConnectionConfigForSalesChannel,
  getCustomerConversations,
} from './graphql/queries';
import {
  transformPayloadForChannelPlatform,
  transformChatContactDetailsForSalesChannel,
  constructMetadata,
  getAnonymousId,
  getChatUrlIdentifier,
} from './utils';
import ChatWidgetButton from './components/ChatWidgetButton';
import {
  sendCustomerJoinedConversationOmnichannelEvent,
  sendOmnichannelChatConnectionConfigGenerationSuccessEvent,
  sendOmnichannelChatConnectionRefreshSuccessEvent,
  sendOmnichannelCommunicationHubErrorEvent,
  sendOmnichannelTokenGenerationSuccessEvent,
  sendCustomerConversationTabClosedOmnichannelEvent,
} from './analytics';

interface SalesChannelHubContentProps {
  email?: string;
  userName?: string;
  isAuthenticated?: boolean;
  visible?: boolean;
}

export const SalesChannelHubContent = ({
  email,
  isAuthenticated,
  visible = true,
}: SalesChannelHubContentProps) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formValues = useMemo(
    () => ({
      details: 'Support inquiry from Shopping Cart',
      contactEmail: email,
      isAuthenticated,
      timezone,
    }),
    [email, isAuthenticated, timezone],
  );

  const [isWidgetOpen, setIsWidgetOpen] = useState<boolean>(false);

  // When the widget becomes non-visible (e.g. navigation away), close the widget UI.
  // The component stays mounted so all cached query data is preserved.
  useEffect(() => {
    if (!visible) {
      setIsWidgetOpen(false);
    }
  }, [visible]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sendCustomerConversationTabClosedOmnichannelEvent();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      sendCustomerConversationTabClosedOmnichannelEvent();
    };
  }, []);

  const {
    error: channelPlatformTokenError,
    data: channelPlatformToken,
    loading: channelPlatformTokenLoading,
  } = useQuery(getChannelPlatformTokenByClientName, {
    variables: {
      clientName: 'sales',
    },
  });

  const [
    fetchChatConnectionConfig,
    {
      data: chatConnectionConfigResponse,
      loading: chatConnectionConfigLoading,
      error: chatConnectionConfigError,
    },
  ] = useLazyQuery(getChatConnectionConfigForSalesChannel);

  const [fetchCustomerConversations, { data: customerConversationsResponse }] = useLazyQuery(
    getCustomerConversations,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const isRefreshed =
    !!customerConversationsResponse &&
    customerConversationsResponse.channelPlatform_getCustomerConversations?.conversations?.length >
      0;

  useEffect(() => {
    if (channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName && isWidgetOpen) {
      const anonymousId = getAnonymousId();
      fetchCustomerConversations({
        variables: {
          anonymousId,
        },
        context: {
          headers: {
            'X-Client-Hash': channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName,
          },
        },
      });
    }
  }, [
    channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName,
    fetchCustomerConversations,
    isWidgetOpen,
  ]);

  useEffect(() => {
    if (
      !isRefreshed &&
      channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName &&
      !chatConnectionConfigResponse &&
      isWidgetOpen
    ) {
      const chatAttributes = getChatUrlIdentifier();
      const anonymousId = getAnonymousId();
      fetchChatConnectionConfig({
        variables: {
          requestType: 'CHAT',
          payload: transformPayloadForChannelPlatform(formValues),
          metadata: constructMetadata(formValues),
          attributes: chatAttributes,
          anonymousId,
        },
        fetchPolicy: 'cache-and-network',
        context: {
          headers: {
            'X-Client-Hash': channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName,
          },
        },
      });
    }
  }, [
    channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName,
    chatConnectionConfigResponse,
    fetchChatConnectionConfig,
    formValues,
    isWidgetOpen,
    isRefreshed,
  ]);

  const isLoading = channelPlatformTokenLoading || chatConnectionConfigLoading;
  const errorOccurred = channelPlatformTokenError || chatConnectionConfigError;

  const filteredChannelPlatformChannels = useMemo(() => ['CHAT', 'TICKET'], []);

  useEffect(() => {
    const createContactValue = chatConnectionConfigResponse?.channelPlatform_createContact?.value;
    if (createContactValue?.contactId) {
      sendCustomerJoinedConversationOmnichannelEvent({
        conversationId: createContactValue.contactId,
      });
    }
  }, [chatConnectionConfigResponse]);

  useEffect(() => {
    if (channelPlatformTokenError) {
      sendOmnichannelCommunicationHubErrorEvent({
        typeOfError: 'channelPlatformTokenError',
        errorMessage: channelPlatformTokenError.message,
      });
    }
  }, [channelPlatformTokenError]);

  useEffect(() => {
    if (chatConnectionConfigError) {
      sendOmnichannelCommunicationHubErrorEvent({
        typeOfError: 'chatConnectionConfigError',
        errorMessage: chatConnectionConfigError.message,
      });
    }
  }, [chatConnectionConfigError]);

  useEffect(() => {
    if (channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName) {
      sendOmnichannelTokenGenerationSuccessEvent({
        supportedChannels: filteredChannelPlatformChannels,
      });
    }
  }, [
    channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName,
    filteredChannelPlatformChannels,
  ]);

  useEffect(() => {
    const createContact = chatConnectionConfigResponse?.channelPlatform_createContact;
    const value = createContact?.value;
    if (createContact && value && value.contactId) {
      sendOmnichannelChatConnectionConfigGenerationSuccessEvent({
        contactId: value.contactId,
        participantId: value.participantId,
        region: value.region,
      });
    }
  }, [chatConnectionConfigResponse]);

  useEffect(() => {
    const conversations =
      customerConversationsResponse?.channelPlatform_getCustomerConversations?.conversations ?? [];
    if (conversations.length > 0) {
      sendOmnichannelChatConnectionRefreshSuccessEvent({
        conversations: conversations.length,
        allConversationIds: conversations.join(',') || '',
        conversationId: conversations[0] || '',
      });
    }
  }, [customerConversationsResponse]);

  const chatContainerWidth = '400px';
  const chatContainerHeight = '700px';

  // When not visible, render nothing — the component stays mounted (preserving
  // cached token, chat config, and conversation data) but produces no UI.
  if (!visible) {
    return null;
  }

  const handleChatButtonClick = () => {
    setIsWidgetOpen(true);
  };

  const isWidgetReady = (channelPlatformToken && chatConnectionConfigResponse) || errorOccurred;

  return (
    <>
      {!isWidgetReady && (
        <ChatWidgetButton onClick={handleChatButtonClick} loading={isWidgetOpen} />
      )}
      {isWidgetOpen && isWidgetReady && (
        <CustomerComponent
          env={config.omniChannelEnvironment as Environments}
          graphqlBaseUrl={
            process.env.OMNICHANNEL_GRAPHQL_ENDPOINT || config.omniChannelGraphQLUrl || ''
          }
          clientDetails={{
            clientId: 'sales',
            clientFormKey: 'cart-web-app',
            isAuthenticated: !!isAuthenticated,
          }}
          clientIdentityHash={channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName}
          chatCapabilityEnabled
          formPayload={transformPayloadForChannelPlatform(formValues)}
          customerImageURL={undefined}
          chatContactDetails={transformChatContactDetailsForSalesChannel(
            chatConnectionConfigResponse,
          )}
          metadata={constructMetadata(formValues)}
          supportedChannels={filteredChannelPlatformChannels}
          isRefreshed={isRefreshed}
          customerConversations={customerConversationsResponse}
          accountId={undefined}
          chatContainerStyles={{
            width: chatContainerWidth,
            height: chatContainerHeight,
          }}
          enableRichText={false}
          displayMode={DisplayMode.FLOATING}
          defaultFloatingWidgetOpen
          enableChatConnectionBrokenHandling={false}
          isLoading={isLoading}
          showErrorBanner={!!errorOccurred}
          enableCustomerTextAreaInputBox={false}
          enableEmailInputBox={false}
          isCustomBotEnabled={true}
        />
      )}
    </>
  );
};

interface SalesChannelHubProps {
  email?: string;
  userName?: string;
  isAuthenticated?: boolean;
  visible?: boolean;
}

export const SalesChannelHub = ({
  email,
  userName,
  isAuthenticated,
  visible,
}: SalesChannelHubProps) => {
  return (
    <ApolloProvider client={omniChannelApolloClient}>
      <SalesChannelHubContent
        email={email}
        userName={userName}
        isAuthenticated={isAuthenticated}
        visible={visible}
      />
    </ApolloProvider>
  );
};

export default SalesChannelHub;
